import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare, X, ChevronLeft, Send, Paperclip, Trash2, Loader2,
  Users, Hash, FileText, ImageIcon, Shield, Bell, BellOff, Pin, PinOff,
  Eye, EyeOff, Smile,
} from 'lucide-react';
import EmojiPicker, { EmojiStyle, Theme } from 'emoji-picker-react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';

const API = process.env.REACT_APP_BACKEND_URL;
const MANAGER_ROLES = ['Admin', 'Manager'];
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '✅'];

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Render message text with @mentions highlighted (matched against known names). */
const renderContent = (text, names) => {
  if (!text) return null;
  if (!names || names.length === 0) return text;
  const sorted = [...names].sort((a, b) => b.length - a.length).map(escapeRe);
  const re = new RegExp(`@(${sorted.join('|')})(?=\\b|\\s|$)`, 'g');
  const out = [];
  let last = 0; let mt; let key = 0;
  while ((mt = re.exec(text)) !== null) {
    if (mt.index > last) out.push(text.slice(last, mt.index));
    out.push(
      <span key={`m${key++}`} className="text-cyan-300 font-semibold bg-cyan-400/10 rounded px-0.5">{mt[0]}</span>
    );
    last = mt.index + mt[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

/**
 * Staff Chat Room drawer. The open/close + unread + notifications live in
 * ChatContext (the trigger now sits in the top-right header). This component
 * renders the sliding drawer: company Room + DMs, @mention autocomplete,
 * attachments, and message delete.
 */
const ChatRoom = () => {
  const { user, token } = useAuth();
  const { open, closeChat, unread, refreshUnread, muted, toggleMute, currentLocation,
          targetScope, consumeTargetScope, reportActiveScope } = useChat();

  const [view, setView] = useState('room'); // 'room' | 'dmlist' | 'convo'
  const [activeUser, setActiveUser] = useState(null);
  // Gate message loading until the saved view is restored on open — otherwise
  // the default 'room' scope loads (and gets marked seen) for a split second
  // even when the drawer is about to open on a DM, silently clearing the
  // room's unread badge.
  const [viewRestored, setViewRestored] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [members, setMembers] = useState([]); // for @mention autocomplete
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  const [input, setInput] = useState('');
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [otherLastSeenAt, setOtherLastSeenAt] = useState(null); // DM read-receipt (managers)

  // @mention autocomplete
  const [mentionQuery, setMentionQuery] = useState(null); // null = closed
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const lastSeenIdRef = useRef(null);
  // Auto-scroll bookkeeping: only stick to the bottom when the user is already
  // near it, so reading history / a pinned message above isn't interrupted.
  const atBottomRef = useRef(true);
  const forceScrollRef = useRef(true); // jump to bottom on first load of a scope
  const prevNewestIdRef = useRef(null);

  // Pinned messages (Company Room) — glowing "Check pinned" bar + cycle-jump.
  const [pinned, setPinned] = useState([]);
  const [pinnedHasNew, setPinnedHasNew] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const pinCycleRef = useRef(0);
  const highlightTimer = useRef(null);

  // Managers: hide my online status from everyone ("appear offline").
  const [appearOffline, setAppearOffline] = useState(false);

  const isManager = MANAGER_ROLES.includes(user?.role);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const memberNames = members.map((m) => m.name);

  const scopeOf = useCallback(() => {
    if (view === 'convo' && activeUser) return { scope: 'dm', user_id: activeUser.id };
    if (view === 'room') return { scope: 'room' };
    return null;
  }, [view, activeUser]);

  const scrollToBottom = useCallback((smooth = false) => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    });
  }, []);

  // Track whether the user is parked at the bottom of the message list.
  const handleMessagesScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  const fetchContacts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/chat/contacts`, authHeader);
      setContacts(res.data.contacts || []);
    } catch (e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchMembers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/chat/members`, authHeader);
      setMembers(res.data.members || []);
    } catch (e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchPinned = useCallback(async () => {
    if (!token) return;
    let urlq = `${API}/api/chat/pinned`;
    if (view === 'convo' && activeUser) urlq += `?scope=dm&user_id=${activeUser.id}`;
    else if (view !== 'room') { setPinned([]); setPinnedHasNew(false); return; }
    try {
      const res = await axios.get(urlq, authHeader);
      setPinned(res.data.pinned || []);
      setPinnedHasNew(!!res.data.has_new);
    } catch (e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, view, activeUser?.id]);

  const markPinnedSeen = useCallback(async () => {
    setPinnedHasNew(false);
    let urlq = `${API}/api/chat/pinned/seen`;
    if (view === 'convo' && activeUser) urlq += `?scope=dm&user_id=${activeUser.id}`;
    try { await axios.post(urlq, {}, authHeader); } catch (e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, view, activeUser?.id]);

  const markSeen = useCallback(async (scopeBody) => {
    try {
      await axios.post(`${API}/api/chat/seen`, scopeBody, authHeader);
      refreshUnread();
    } catch (e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshUnread]);

  const loadMessages = useCallback(async (opts = {}) => {
    const s = scopeOf();
    if (!s) return;
    const { silent = false } = opts;
    if (!silent) setLoadingMsgs(true);
    try {
      const params = { scope: s.scope, limit: 50 };
      if (s.user_id) params.user_id = s.user_id;
      const res = await axios.get(`${API}/api/chat/messages`, { ...authHeader, params });
      const msgs = res.data.messages || [];
      setHasMore(res.data.has_more);
      setOtherLastSeenAt(res.data.other_last_seen_at || null);
      setMessages((prev) => {
        // Full (non-silent) load or empty list: replace with the fresh batch.
        if (!silent || prev.length === 0) return msgs;
        // Silent poll: merge so we preserve older history already loaded via
        // "Load older messages" while still picking up new/edited/deleted ones.
        const map = new Map();
        for (const m of prev) map.set(m.id, m);
        for (const m of msgs) map.set(m.id, m); // latest batch wins (edits/deletes/reactions)
        const merged = Array.from(map.values()).sort(
          (a, b) => (a.created_at || '').localeCompare(b.created_at || '')
        );
        const unchanged =
          merged.length === prev.length &&
          merged.every((m, i) => JSON.stringify(m) === JSON.stringify(prev[i]));
        return unchanged ? prev : merged;
      });
      const newestId = msgs.length ? msgs[msgs.length - 1].id : null;
      if (newestId && newestId !== lastSeenIdRef.current) {
        lastSeenIdRef.current = newestId;
        markSeen(s.scope === 'room' ? { scope: 'room' } : { scope: 'dm', user_id: s.user_id });
        // Note: scrolling is handled by the render-driven effect below so it
        // only sticks to the bottom when the user is already there.
      }
    } catch (e) {
      if (!silent) toast.error('Could not load messages');
    } finally {
      if (!silent) setLoadingMsgs(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeOf, markSeen, scrollToBottom, token]);

  const loadOlder = useCallback(async () => {
    const s = scopeOf();
    if (!s || !messages.length) return;
    try {
      const params = { scope: s.scope, limit: 50, before: messages[0].created_at };
      if (s.user_id) params.user_id = s.user_id;
      const res = await axios.get(`${API}/api/chat/messages`, { ...authHeader, params });
      const older = res.data.messages || [];
      setHasMore(res.data.has_more);
      if (older.length) {
        const el = scrollRef.current;
        const prevHeight = el ? el.scrollHeight : 0;
        setMessages((prev) => [...older, ...prev]);
        requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - prevHeight; });
      }
    } catch (e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeOf, messages, token]);

  // Load members once when the drawer opens
  useEffect(() => { if (open) fetchMembers(); }, [open, fetchMembers]);

  // Managers: load my appear-offline setting when the drawer opens.
  useEffect(() => {
    if (!open || !isManager || !token) return;
    axios.get(`${API}/api/chat/presence/me`, authHeader)
      .then((res) => setAppearOffline(!!res.data.appear_offline))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isManager, token]);

  const toggleAppearOffline = async () => {
    const next = !appearOffline;
    setAppearOffline(next);
    try {
      await axios.post(`${API}/api/chat/presence/appear-offline`, { enabled: next }, authHeader);
      toast.success(next ? 'You now appear offline to others' : 'You now appear online');
    } catch (e) {
      setAppearOffline(!next);
      toast.error(e.response?.data?.detail || 'Could not update visibility');
    }
  };

  // (Re)load when the active scope changes (only after the saved view has
  // been restored for this open cycle — see viewRestored above)
  useEffect(() => {
    if (!open || !viewRestored) return;
    lastSeenIdRef.current = null;
    pinCycleRef.current = 0;
    prevNewestIdRef.current = null;
    forceScrollRef.current = true; // jump to bottom once the first batch renders
    atBottomRef.current = true;
    setMessages([]);
    setPinned([]);
    setPinnedHasNew(false);
    if (view === 'room' || (view === 'convo' && activeUser)) loadMessages();
    if (view === 'room' || (view === 'convo' && activeUser)) fetchPinned();
    if (view === 'dmlist') fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, viewRestored, view, activeUser?.id]);

  // Auto-scroll: stick to the bottom on first load of a scope, and on a NEW
  // message only when the user is already near the bottom (so reading history
  // or a pinned message above is never interrupted).
  useEffect(() => {
    if (!messages.length) return;
    const newestId = messages[messages.length - 1].id;
    if (forceScrollRef.current) {
      forceScrollRef.current = false;
      prevNewestIdRef.current = newestId;
      scrollToBottom(false);
      return;
    }
    if (newestId !== prevNewestIdRef.current) {
      prevNewestIdRef.current = newestId;
      if (atBottomRef.current) scrollToBottom(true);
    }
  }, [messages, scrollToBottom]);

  // Poll active conversation
  useEffect(() => {
    if (!open || (view !== 'room' && view !== 'convo')) return;
    const id = setInterval(() => {
      loadMessages({ silent: true });
      fetchPinned();
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view, activeUser?.id, loadMessages, fetchPinned]);

  // Refresh DM pips while browsing the list
  useEffect(() => {
    if (open && view === 'dmlist') {
      const id = setInterval(fetchContacts, 5000);
      return () => clearInterval(id);
    }
  }, [open, view, fetchContacts]);

  // Restore the last-viewed scope each time the drawer opens (Company Room or
  // the last DM). Unread ROOM messages take priority — land on the room so
  // announcements (online orders etc.) are actually seen. A pending
  // targetScope (from a mention/DM alert) overrides this via the effect below.
  useEffect(() => {
    if (!open) { setViewRestored(false); return; }
    try {
      const saved = JSON.parse(localStorage.getItem('chat_last_view') || 'null');
      if ((unread?.room || 0) > 0) {
        setActiveUser(null);
        setView('room');
      } else if (saved && saved.view === 'convo' && saved.user?.id) {
        setActiveUser(saved.user);
        setView('convo');
      } else if (saved && saved.view === 'dmlist') {
        setActiveUser(null);
        setView('dmlist');
      } else {
        setActiveUser(null);
        setView('room');
      }
    } catch {
      setActiveUser(null);
      setView('room');
    }
    setViewRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Remember the last-viewed scope so reopening the drawer returns to it.
  useEffect(() => {
    if (!open) return;
    let saved;
    if (view === 'convo' && activeUser) saved = { view: 'convo', user: activeUser };
    else if (view === 'dmlist') saved = { view: 'dmlist' };
    else saved = { view: 'room' };
    localStorage.setItem('chat_last_view', JSON.stringify(saved));
  }, [open, view, activeUser]);

  // Jump to a specific conversation when the mention popup asks us to
  useEffect(() => {
    if (!open || !targetScope) return;
    if (targetScope.type === 'dm' && targetScope.user) {
      const known = contacts.find((c) => c.id === targetScope.user.id);
      setActiveUser(known || targetScope.user);
      setView('convo');
    } else {
      setActiveUser(null);
      setView('room');
    }
    consumeTargetScope();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetScope]);

  // Report the scope I'm currently viewing so the popup can suppress self-alerts
  useEffect(() => {
    const key = !open ? null
      : view === 'room' ? 'room'
      : (view === 'convo' && activeUser) ? `dm:${activeUser.id}` : null;
    reportActiveScope(key);
  }, [open, view, activeUser, reportActiveScope]);

  const openRoom = () => { setActiveUser(null); setView('room'); };
  const openDmList = () => { setActiveUser(null); setView('dmlist'); fetchContacts(); };
  const openConvo = (c) => { setActiveUser(c); setView('convo'); };

  // Scroll the message list to a specific message and briefly highlight it.
  // If the message isn't loaded yet, page back through older messages first.
  const jumpToMessage = useCallback((id) => {
    const tryScroll = (attemptsLeft) => {
      const container = scrollRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-msg-id="${id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightId(id);
        if (highlightTimer.current) clearTimeout(highlightTimer.current);
        highlightTimer.current = setTimeout(() => setHighlightId((h) => (h === id ? null : h)), 2400);
      } else if (attemptsLeft > 0 && hasMore) {
        loadOlder().then(() => setTimeout(() => tryScroll(attemptsLeft - 1), 200));
      }
    };
    tryScroll(10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadOlder]);

  // "Check pinned messages": jump to the most-recent pin, then cycle to the
  // next-most-recent on each subsequent click (wraps around).
  const handleCheckPinned = useCallback(() => {
    if (!pinned.length) return;
    if (pinnedHasNew) markPinnedSeen();
    const idx = pinCycleRef.current % pinned.length;
    const target = pinned[idx];
    pinCycleRef.current = (idx + 1) % pinned.length;
    if (target) jumpToMessage(target.id);
  }, [pinned, pinnedHasNew, markPinnedSeen, jumpToMessage]);

  const togglePin = useCallback(async (m) => {
    try {
      await axios.post(`${API}/api/chat/messages/${m.id}/${m.pinned ? 'unpin' : 'pin'}`, {}, authHeader);
      pinCycleRef.current = 0;
      await fetchPinned();
      loadMessages({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update pin');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, fetchPinned, loadMessages]);

  // ----- @mention autocomplete (Room: anyone · DM: the partner) -----
  const mentionPool = (view === 'convo' && activeUser)
    ? [{ id: activeUser.id, name: activeUser.name, role: activeUser.role, is_manager: MANAGER_ROLES.includes(activeUser.role) }]
    : members;

  // Auto-grow the message box vertically up to 4 lines, then scroll internally.
  const autoResize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    const cs = window.getComputedStyle(el);
    const line = parseFloat(cs.lineHeight) || 20;
    const padding = parseFloat(cs.paddingTop || 0) + parseFloat(cs.paddingBottom || 0);
    const maxH = line * 4 + padding;
    el.style.height = `${Math.min(el.scrollHeight, maxH)}px`;
    el.style.overflowY = el.scrollHeight > maxH ? 'auto' : 'hidden';
  };

  const onInputChange = (e) => {
    const val = e.target.value;
    setInput(val);
    autoResize(e.target);
    if (view !== 'room' && view !== 'convo') { setMentionQuery(null); return; }
    const caret = e.target.selectionStart ?? val.length;
    const upto = val.slice(0, caret);
    const m = upto.match(/(?:^|\s)@([\w]*)$/);
    setMentionQuery(m ? m[1].toLowerCase() : null);
  };

  const insertMention = (name) => {
    const el = inputRef.current;
    const caret = el ? el.selectionStart : input.length;
    const before = input.slice(0, caret).replace(/@([\w]*)$/, `@${name} `);
    const after = input.slice(caret);
    const next = before + after;
    setInput(next);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      el?.focus();
      autoResize(el);
      const pos = before.length;
      el?.setSelectionRange(pos, pos);
    });
  };

  const mentionMatches = mentionQuery !== null
    ? mentionPool.filter((m) => m.name.toLowerCase().includes(mentionQuery)).slice(0, 6)
    : [];

  const computeMentionIds = (text) =>
    mentionPool.filter((m) => new RegExp(`@${escapeRe(m.name)}(?=\\b|\\s|$)`).test(text)).map((m) => m.id);

  const uploadFile = async (file, { rename } = {}) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File too large (max 25MB)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      // Pasted screenshots often have no/blank filename — give them a friendly one.
      const named = rename ? new File([file], rename, { type: file.type }) : file;
      fd.append('file', named);
      const res = await axios.post(`${API}/api/chat/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setPendingAttachment(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'try again';
      toast.error(`Upload failed — ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  const handleAttach = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // Compress attached images too (same proxy size limit applies); leave
    // non-image files (PDFs etc.) untouched.
    if (file.type.startsWith('image/')) {
      const { blob, ext, type } = await normalizePastedImage(file);
      await uploadFile(new File([blob], `image-${Date.now()}.${ext}`, { type: type || 'image/jpeg' }));
    } else {
      await uploadFile(file);
    }
  };

  // Paste an image straight from the clipboard (e.g. a screenshot via Ctrl/Cmd+V).
  // macOS screenshots often land on the clipboard as TIFF or huge Retina images,
  // and detailed screenshots re-encode to multi-MB PNGs that the production proxy
  // can reject (Cloudflare 520). We re-encode whatever we get through a canvas
  // into a compact JPEG so the upload stays small and reliably succeeds.
  const normalizePastedImage = (file) => new Promise((resolve) => {
    const MAX_DIM = 1600;
    let url;
    try {
      url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        try {
          const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff'; // JPEG has no alpha — flatten onto white
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob((blob) => {
            URL.revokeObjectURL(url);
            resolve(blob ? { blob, ext: 'jpg', type: 'image/jpeg' } : { blob: file, ext: 'png', type: file.type });
          }, 'image/jpeg', 0.82);
        } catch (_) { URL.revokeObjectURL(url); resolve({ blob: file, ext: 'png', type: file.type }); }
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve({ blob: file, ext: 'png', type: file.type }); };
      img.src = url;
    } catch (_) { if (url) URL.revokeObjectURL(url); resolve({ blob: file, ext: 'png', type: file.type }); }
  });

  const onPaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    // getAsFile() must be called synchronously, before any await.
    const imageItems = [...items].filter((it) => it.kind === 'file' && it.type.startsWith('image/'));
    if (!imageItems.length) return;
    // Prefer a directly-decodable format over TIFF (Chrome can't decode TIFF).
    const preferred = imageItems.find((it) => /png|jpe?g|webp|gif/.test(it.type)) || imageItems[0];
    const file = preferred.getAsFile();
    if (!file) return;
    e.preventDefault();
    const { blob, ext, type } = await normalizePastedImage(file);
    const outFile = new File([blob], `pasted-${Date.now()}.${ext}`, { type: type || 'image/jpeg' });
    await uploadFile(outFile);
  };

  const sendMessage = async () => {
    const s = scopeOf();
    if (!s) return;
    const content = input.trim();
    if (!content && !pendingAttachment) return;
    setSending(true);
    try {
      const body = {
        scope: s.scope,
        recipient_id: s.scope === 'dm' ? s.user_id : undefined,
        content: content || undefined,
        mention_ids: content ? computeMentionIds(content) : undefined,
        sender_location: currentLocation || undefined,
        attachment_url: pendingAttachment?.url,
        attachment_type: pendingAttachment?.type,
        attachment_name: pendingAttachment?.name,
      };
      await axios.post(`${API}/api/chat/messages`, body, authHeader);
      setInput('');
      setPendingAttachment(null);
      setMentionQuery(null);
      if (inputRef.current) { inputRef.current.style.height = 'auto'; inputRef.current.style.overflowY = 'hidden'; }
      atBottomRef.current = true; // sending always returns the user to the bottom
      await loadMessages({ silent: true });
      scrollToBottom(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    if (!el) { setInput((v) => v + emoji); return; }
    const start = el.selectionStart ?? input.length;
    const end = el.selectionEnd ?? input.length;
    const next = input.slice(0, start) + emoji + input.slice(end);
    setInput(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      try { el.setSelectionRange(pos, pos); } catch (_) {}
    });
  };

  const reactToMessage = async (messageId, emoji) => {
    try {
      const res = await axios.post(`${API}/api/chat/messages/${messageId}/react`,
        { emoji }, authHeader);
      setMessages((prev) => prev.map((m) =>
        m.id === messageId ? { ...m, reactions: res.data.reactions } : m));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not react');
    }
  };

  const deleteMessage = async (id) => {
    try {
      await axios.delete(`${API}/api/chat/messages/${id}`, authHeader);
      loadMessages({ silent: true });
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  const onKeyDown = (e) => {
    if (mentionQuery !== null && mentionMatches.length && e.key === 'Enter') {
      e.preventDefault();
      insertMention(mentionMatches[0].name);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    if (e.key === 'Escape') setMentionQuery(null);
  };

  if (!token || !user) return null;

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[59] bg-black/30" onClick={closeChat} data-testid="chat-backdrop" />
      )}

      <div
        data-testid="chat-drawer"
        className={`fixed right-0 top-0 h-full w-full max-w-[400px] z-[60] bg-slate-900 border-l border-slate-700 shadow-2xl flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-950/60">
          <div className="flex items-center gap-2">
            <MessageSquare size={18} className="text-emerald-400" />
            <span className="font-bold text-white">Chat Room</span>
          </div>
          <div className="flex items-center gap-1">
            {isManager && (
              <button
                onClick={toggleAppearOffline}
                data-testid="chat-appear-offline-toggle"
                title={appearOffline ? 'You appear offline — click to show as online' : 'Appear offline to others'}
                className={`p-1.5 rounded hover:bg-white/10 ${appearOffline ? 'text-gray-500' : 'text-emerald-400'}`}
              >
                {appearOffline ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            )}
            <button
              onClick={toggleMute}
              data-testid="chat-mute-toggle"
              title={muted ? 'Unmute notification sound' : 'Mute notification sound'}
              className={`p-1.5 rounded hover:bg-white/10 ${muted ? 'text-gray-500' : 'text-emerald-400'}`}
            >
              {muted ? <BellOff size={16} /> : <Bell size={16} />}
            </button>
            <button onClick={closeChat} data-testid="chat-close" className="text-gray-400 hover:text-white p-1 rounded hover:bg-white/10">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          <button
            onClick={openRoom}
            data-testid="chat-tab-room"
            className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${view === 'room' ? 'text-emerald-400 border-b-2 border-emerald-400 bg-white/5' : 'text-gray-400 hover:text-white'}`}
          >
            <Hash size={15} /> Company Room
            {unread.room > 0 && <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-bold flex items-center justify-center">{unread.room}</span>}
          </button>
          <button
            onClick={openDmList}
            data-testid="chat-tab-dms"
            className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${(view === 'dmlist' || view === 'convo') ? 'text-cyan-400 border-b-2 border-cyan-400 bg-white/5' : 'text-gray-400 hover:text-white'}`}
          >
            <Users size={15} /> Direct Messages
            {unread.dm_total > 0 && <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-bold flex items-center justify-center">{unread.dm_total}</span>}
          </button>
        </div>

        {view === 'dmlist' ? (
          <DmList contacts={contacts} dmByUser={unread.dm_by_user} isManager={isManager} onOpen={openConvo} />
        ) : (
          <>
            {view === 'convo' && activeUser && (
              <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700 bg-slate-800/40">
                <button onClick={openDmList} data-testid="chat-convo-back" className="text-gray-400 hover:text-white p-1 rounded hover:bg-white/10">
                  <ChevronLeft size={18} />
                </button>
                <div className="relative">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center text-white text-xs font-bold">
                    {(activeUser.name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-slate-900 ${activeUser.online ? 'bg-emerald-400' : 'bg-gray-500'}`} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium truncate flex items-center gap-1">
                    {activeUser.name}
                    {MANAGER_ROLES.includes(activeUser.role) && <Shield size={12} className="text-amber-400" />}
                  </div>
                  <div className="text-[11px] text-gray-400 truncate">
                    {activeUser.online ? 'Online' : 'Offline'}
                    {activeUser.location ? ` · ${activeUser.location}` : ''}
                  </div>
                </div>
              </div>
            )}

            <div ref={scrollRef} onScroll={handleMessagesScroll} data-testid="chat-messages" className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
              {(view === 'room' || view === 'convo') && pinned.length > 0 && (
                <div className="sticky -top-3 z-10 -mx-3 px-3 pb-2 pt-1 bg-gradient-to-b from-slate-900 via-slate-900/95 to-transparent">
                  <button
                    onClick={handleCheckPinned}
                    data-testid="chat-check-pinned"
                    style={{ boxShadow: '0 0 14px rgba(251,191,36,0.55)' }}
                    className={`w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-bold text-amber-950 bg-gradient-to-r from-amber-300 to-yellow-400 border border-amber-200 transition-transform hover:scale-[1.01] ${pinnedHasNew ? 'animate-pulse' : ''}`}
                  >
                    <Pin size={15} className="fill-amber-950" />
                    Check pinned messages
                    <span className="text-amber-800/80">({pinned.length})</span>
                    {pinnedHasNew && (
                      <span data-testid="chat-pinned-new" className="ml-1 px-1.5 py-0.5 rounded-full bg-pink-500 text-white text-[10px] font-extrabold tracking-wide">NEW</span>
                    )}
                  </button>
                </div>
              )}
              {loadingMsgs ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gray-500" /></div>
              ) : messages.length === 0 ? (
                <div className="text-center text-gray-500 text-sm py-10">
                  {view === 'room' ? 'No messages yet. Say hi to the team! 👋' : `Start a conversation with ${activeUser?.name || ''}.`}
                </div>
              ) : (
                <>
                  {hasMore && (
                    <div className="text-center">
                      <button onClick={loadOlder} className="text-xs text-gray-400 hover:text-white underline">Load older messages</button>
                    </div>
                  )}
                  {(() => {
                    // Read receipt (managers, DM only): find the last message I
                    // sent that the other person has read, to label it "Seen".
                    let seenMsgId = null;
                    if (view === 'convo' && otherLastSeenAt) {
                      for (const m of messages) {
                        if (m.is_mine && !m.deleted && m.created_at && m.created_at <= otherLastSeenAt) {
                          seenMsgId = m.id;
                        }
                      }
                    }
                    return messages.map((m) => (
                    <MessageBubble
                      key={m.id}
                      m={m}
                      canDelete={m.is_mine || isManager}
                      onDelete={deleteMessage}
                      showSender={view === 'room' && !m.is_mine}
                      memberNames={memberNames}
                      canPin={!m.deleted && ((view === 'room' && isManager) || view === 'convo')}
                      onTogglePin={togglePin}
                      onReact={reactToMessage}
                      highlighted={highlightId === m.id}
                      seenAt={m.id === seenMsgId ? otherLastSeenAt : null}
                    />
                  ));
                  })()}
                </>
              )}
            </div>

            {/* Composer */}
            <div className="relative border-t border-slate-700 p-2.5 bg-slate-950/40">
              {/* @mention dropdown */}
              {mentionQuery !== null && mentionMatches.length > 0 && (
                <div className="absolute bottom-full left-2.5 mb-1 w-64 bg-slate-800 border border-slate-600 rounded-lg shadow-xl overflow-hidden z-10" data-testid="chat-mention-dropdown">
                  {mentionMatches.map((m) => (
                    <button
                      key={m.id}
                      onMouseDown={(e) => { e.preventDefault(); insertMention(m.name); }}
                      data-testid={`chat-mention-${m.id}`}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 text-left"
                    >
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center text-white text-[10px] font-bold">{m.name.slice(0, 1).toUpperCase()}</div>
                      <span className="text-sm text-white truncate flex-1">{m.name}</span>
                      {m.is_manager && <Shield size={11} className="text-amber-400" />}
                    </button>
                  ))}
                </div>
              )}

              {pendingAttachment && (
                <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded bg-slate-800 text-xs text-gray-200" data-testid="chat-pending-attachment">
                  {pendingAttachment.type === 'image' ? <ImageIcon size={14} className="text-emerald-400" /> : <FileText size={14} className="text-cyan-400" />}
                  <span className="truncate flex-1">{pendingAttachment.name}</span>
                  <button onClick={() => setPendingAttachment(null)} className="text-gray-400 hover:text-white"><X size={14} /></button>
                </div>
              )}
              <div className="flex items-end gap-2 relative">
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleAttach} data-testid="chat-file-input" />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="chat-attach-btn"
                  className="text-gray-400 hover:text-emerald-400 p-2 rounded hover:bg-white/5 disabled:opacity-50"
                  title="Attach image or file"
                >
                  {uploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
                </button>
                <button
                  onClick={() => setShowEmojiPicker((v) => !v)}
                  data-testid="chat-emoji-btn"
                  className={`p-2 rounded hover:bg-white/5 ${showEmojiPicker ? 'text-amber-400' : 'text-gray-400 hover:text-amber-400'}`}
                  title="Emoji"
                >
                  <Smile size={18} />
                </button>
                {showEmojiPicker && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowEmojiPicker(false)} data-testid="chat-emoji-overlay" />
                    <div className="absolute bottom-12 left-0 z-50" data-testid="chat-emoji-picker">
                      <EmojiPicker
                        theme={Theme.DARK}
                        emojiStyle={EmojiStyle.NATIVE}
                        width={320}
                        height={380}
                        lazyLoadEmojis
                        searchPlaceholder="Search emoji"
                        previewConfig={{ showPreview: false }}
                        onEmojiClick={(data) => { insertEmoji(data.emoji); setShowEmojiPicker(false); }}
                      />
                    </div>
                  </>
                )}
                <textarea
                  ref={inputRef}
                  data-testid="chat-input"
                  value={input}
                  onChange={onInputChange}
                  onKeyDown={onKeyDown}
                  onPaste={onPaste}
                  placeholder={view === 'room' ? 'Message the team…  (@ to mention)' : `Message ${activeUser?.name || ''}…`}
                  rows={1}
                  className="flex-1 resize-none bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 max-h-28"
                />
                <button
                  onClick={sendMessage}
                  disabled={sending || (!input.trim() && !pendingAttachment)}
                  data-testid="chat-send-btn"
                  className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
                >
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const DmList = ({ contacts, dmByUser, isManager, onOpen }) => {
  const onlineCount = contacts.filter((c) => c.online).length;
  return (
    <div className="flex-1 overflow-y-auto" data-testid="chat-dm-list">
      <div className="px-4 py-2 flex items-center justify-between text-[11px] uppercase tracking-wide text-gray-500">
        <span>{isManager ? 'Message anyone' : 'Message a manager'}</span>
        <span className="flex items-center gap-1 normal-case" data-testid="chat-online-count">
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          {onlineCount} online
        </span>
      </div>
      {contacts.length === 0 ? (
        <div className="text-center text-gray-500 text-sm py-10">No one available to message.</div>
      ) : (
        contacts.map((c) => {
          const unread = dmByUser?.[c.id] || c.unread || 0;
          return (
            <button
              key={c.id}
              onClick={() => onOpen(c)}
              data-testid={`chat-contact-${c.id}`}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left border-b border-slate-800"
            >
              <div className="relative flex-shrink-0">
                <div className={`w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center text-white text-sm font-bold ${c.online ? '' : 'opacity-60'}`}>
                  {(c.name || '?').slice(0, 1).toUpperCase()}
                </div>
                <span
                  data-testid={`chat-presence-${c.id}`}
                  title={c.online ? 'Online' : 'Offline'}
                  className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-slate-900 ${c.online ? 'bg-emerald-400' : 'bg-gray-500'}`}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white font-medium truncate flex items-center gap-1">
                  {c.name}
                  {c.location && <span className="text-gray-400 font-normal">- {c.location}</span>}
                  {c.is_manager && <Shield size={12} className="text-amber-400" />}
                </div>
                <div className="text-[11px] flex items-center gap-1.5">
                  <span className={c.online ? 'text-emerald-400' : 'text-gray-500'}>{c.online ? 'Online' : 'Offline'}</span>
                  <span className="text-gray-600">·</span>
                  <span className="text-gray-400">{c.role}</span>
                </div>
              </div>
              {unread > 0 && (
                <span className="min-w-[20px] h-5 px-1 rounded-full bg-pink-500 text-white text-[11px] font-bold flex items-center justify-center">{unread}</span>
              )}
            </button>
          );
        })
      )}
    </div>
  );
};

// Friendly date + time for a message timestamp: "Today 3:42 PM",
// "Yesterday 9:01 AM", or "Jun 17, 3:42 PM" (adds year if not the current one).
const formatMsgDateTime = (raw) => {
  if (!raw) return '';
  const d = new Date(raw + (raw.endsWith('Z') ? '' : 'Z'));
  if (isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const now = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (dayDiff === 0) return `Today ${time}`;
  if (dayDiff === 1) return `Yesterday ${time}`;
  const opts = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return `${d.toLocaleDateString([], opts)}, ${time}`;
};

const formatMsgTime = (raw) => {
  if (!raw) return '';
  const d = new Date(raw + (raw.endsWith('Z') ? '' : 'Z'));
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const MessageBubble = ({ m, canDelete, onDelete, showSender, memberNames, canPin, onTogglePin, onReact, highlighted, seenAt }) => {
  const mine = m.is_mine;
  const [showReactPicker, setShowReactPicker] = useState(false);
  const reactions = m.reactions || [];
  return (
    <div data-msg-id={m.id} className={`group flex ${mine ? 'justify-end' : 'justify-start'} ${highlighted ? 'rounded-xl ring-2 ring-amber-400 bg-amber-400/10 transition-all duration-300' : 'transition-all duration-300'}`}>
      <div className={`max-w-[80%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
        {showSender && (
          <span className="text-[11px] text-gray-400 mb-0.5 ml-1 flex items-center gap-1">
            {m.sender_name}
            {MANAGER_ROLES.includes(m.sender_role) && <Shield size={10} className="text-amber-400" />}
            {m.sender_location && <span className="text-gray-500">- {m.sender_location}</span>}
          </span>
        )}
        {m.pinned && !m.deleted && (
          <span className={`text-[10px] text-amber-400 font-semibold mb-0.5 flex items-center gap-1 ${mine ? 'mr-1 justify-end' : 'ml-1'}`} data-testid={`chat-pinned-tag-${m.id}`}>
            <Pin size={10} className="fill-amber-400" /> Pinned
          </span>
        )}
        <div className="flex items-end gap-1">
          {mine && !m.deleted && (canDelete || canPin || onReact) && (
            <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
              {onReact && (
                <div className="relative">
                  <button onClick={() => setShowReactPicker((v) => !v)} data-testid={`chat-react-toggle-${m.id}`} className="text-gray-500 hover:text-amber-400 p-1" title="React">
                    <Smile size={13} />
                  </button>
                  {showReactPicker && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowReactPicker(false)} />
                      <div className="absolute bottom-7 right-0 z-50 flex gap-0.5 bg-slate-900 border border-slate-700 rounded-full px-1.5 py-1 shadow-xl" data-testid={`chat-react-picker-${m.id}`}>
                        {QUICK_REACTIONS.map((e) => (
                          <button key={e} onClick={() => { onReact(m.id, e); setShowReactPicker(false); }} className="text-base leading-none hover:scale-125 transition-transform p-0.5" data-testid={`chat-react-${m.id}-${e}`}>{e}</button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              {canPin && (
                <button onClick={() => onTogglePin(m)} data-testid={`chat-pin-toggle-${m.id}`} className={`p-1 ${m.pinned ? 'text-amber-400 hover:text-amber-300' : 'text-gray-500 hover:text-amber-400'}`} title={m.pinned ? 'Unpin' : 'Pin'}>
                  {m.pinned ? <PinOff size={13} /> : <Pin size={13} />}
                </button>
              )}
              {canDelete && (
                <button onClick={() => onDelete(m.id)} className="text-gray-500 hover:text-red-400 p-1" title="Delete">
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          )}
          <div className={`rounded-2xl px-3 py-2 text-sm ${m.deleted ? 'bg-slate-800 text-gray-500 italic' : mine ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-gray-100'}`}>
            {m.deleted ? 'message deleted' : (
              <>
                {m.attachment_url && m.attachment_type === 'image' && (
                  <a href={m.attachment_url} target="_blank" rel="noreferrer" className="block mb-1">
                    <img src={m.attachment_url} alt={m.attachment_name || 'image'} className="rounded-lg max-h-48 max-w-full object-cover" />
                  </a>
                )}
                {m.attachment_url && m.attachment_type !== 'image' && (
                  <a href={m.attachment_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 mb-1 underline decoration-dotted">
                    <FileText size={14} /> <span className="truncate max-w-[200px]">{m.attachment_name || 'file'}</span>
                  </a>
                )}
                {m.content && <div className="whitespace-pre-wrap break-words">{renderContent(m.content, mine ? [] : memberNames)}</div>}
              </>
            )}
          </div>
          {!mine && !m.deleted && (canDelete || canPin || onReact) && (
            <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
              {onReact && (
                <div className="relative">
                  <button onClick={() => setShowReactPicker((v) => !v)} data-testid={`chat-react-toggle-${m.id}`} className="text-gray-500 hover:text-amber-400 p-1" title="React">
                    <Smile size={13} />
                  </button>
                  {showReactPicker && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowReactPicker(false)} />
                      <div className="absolute bottom-7 left-0 z-50 flex gap-0.5 bg-slate-900 border border-slate-700 rounded-full px-1.5 py-1 shadow-xl" data-testid={`chat-react-picker-${m.id}`}>
                        {QUICK_REACTIONS.map((e) => (
                          <button key={e} onClick={() => { onReact(m.id, e); setShowReactPicker(false); }} className="text-base leading-none hover:scale-125 transition-transform p-0.5" data-testid={`chat-react-${m.id}-${e}`}>{e}</button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              {canPin && (
                <button onClick={() => onTogglePin(m)} data-testid={`chat-pin-toggle-${m.id}`} className={`p-1 ${m.pinned ? 'text-amber-400 hover:text-amber-300' : 'text-gray-500 hover:text-amber-400'}`} title={m.pinned ? 'Unpin' : 'Pin'}>
                  {m.pinned ? <PinOff size={13} /> : <Pin size={13} />}
                </button>
              )}
              {canDelete && (
                <button onClick={() => onDelete(m.id)} className="text-gray-500 hover:text-red-400 p-1" title="Delete">
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          )}
        </div>
        {reactions.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${mine ? 'justify-end mr-1' : 'ml-1'}`} data-testid={`chat-reactions-${m.id}`}>
            {reactions.map((r) => (
              <button
                key={r.emoji}
                onClick={() => onReact && onReact(m.id, r.emoji)}
                data-testid={`chat-reaction-chip-${m.id}-${r.emoji}`}
                className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border transition-colors ${r.me ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-200' : 'bg-slate-700/60 border-slate-600 text-gray-300 hover:bg-slate-600/60'}`}
                title={r.me ? 'Remove your reaction' : 'Add reaction'}
              >
                <span className="leading-none">{r.emoji}</span>
                <span className="leading-none">{r.count}</span>
              </button>
            ))}
          </div>
        )}
        <span className={`text-[10px] text-gray-500 mt-0.5 ${mine ? 'mr-1 text-right' : 'ml-1'}`} data-testid={`chat-msg-time-${m.id}`}>
          {formatMsgDateTime(m.created_at)}
        </span>
        {seenAt && (
          <span className="text-[10px] text-emerald-400/90 mt-0.5 mr-1 text-right flex items-center justify-end gap-0.5" data-testid={`chat-seen-${m.id}`}>
            <Eye size={10} /> Seen {formatMsgTime(seenAt)}
          </span>
        )}
      </div>
    </div>
  );
};

export default ChatRoom;
